/**
 * Catalogue of everything the bulk-assignment wizard can flag in an imported
 * file. Mirrors the shape of `alertCatalog.ts`: one entry per code, with the
 * severity that decides whether the match is imported, and the Spanish copy the
 * issues panel renders.
 *
 * Order matters — the panel lists codes in the order declared here, so the
 * blocking ones come first.
 */

import {
  ImportIssueCode,
  ImportIssueMeta,
  ImportIssueSeverity,
  ImportIssueUnit,
} from '../../../types/bulkAssignment';

export const IMPORT_ISSUE_CATALOG: Record<ImportIssueCode, ImportIssueMeta> = {
  // ── Errores: el match no se importa ───────────────────────────────────────
  UNKNOWN_FOLIO: {
    code: 'UNKNOWN_FOLIO',
    severity: 'error',
    scope: 'application',
    countBy: 'application',
    label: 'Folio no existe en este concurso',
    hint: 'La fila se descarta. Verifica que el archivo corresponda a este concurso.',
  },
  WRONG_STATUS: {
    code: 'WRONG_STATUS',
    severity: 'error',
    scope: 'application',
    countBy: 'application',
    label: 'La postulación ya no admite evaluadores',
    hint: 'Cambió de estado después de generarse el archivo. La fila se descarta.',
  },
  SELF_ASSIGNMENT: {
    code: 'SELF_ASSIGNMENT',
    severity: 'error',
    scope: 'application',
    countBy: 'match',
    label: 'El evaluador integra el equipo del proyecto',
    hint: 'No puede evaluar una postulación en la que participa. El match se descarta.',
  },
  EVALUATOR_NOT_FOUND: {
    code: 'EVALUATOR_NOT_FOUND',
    severity: 'error',
    scope: 'evaluator',
    countBy: 'match',
    label: 'Evaluador no registrado en la plataforma',
    hint: 'Créalo primero en el tab Evaluadores y vuelve a importar.',
  },
  EVALUATOR_EXCLUDED: {
    code: 'EVALUATOR_EXCLUDED',
    severity: 'error',
    scope: 'evaluator',
    countBy: 'match',
    label: 'Evaluador excluido de este concurso',
    hint: 'Está marcado como no disponible. El match se descarta.',
  },
  PRIOR_CONFLICT: {
    code: 'PRIOR_CONFLICT',
    severity: 'error',
    scope: 'application',
    countBy: 'match',
    label: 'Ya se excusó o declaró conflicto en esa postulación',
    hint: 'No se le vuelve a invitar. El match se descarta.',
  },
  DUPLICATE_IN_FILE: {
    code: 'DUPLICATE_IN_FILE',
    severity: 'error',
    scope: 'application',
    countBy: 'match',
    label: 'El par postulación y evaluador viene repetido',
    hint: 'Se conserva la primera aparición y se descartan las siguientes.',
  },

  // ── Advertencias: se importa, pero conviene revisarlo ─────────────────────
  NO_COVERAGE: {
    code: 'NO_COVERAGE',
    severity: 'warning',
    scope: 'application',
    countBy: 'application',
    label: 'Postulación sin ningún evaluador',
    hint: 'Quedará sin cobertura. Asígnala a mano con Gestionar Evaluadores.',
  },
  PARTIAL_COVERAGE: {
    code: 'PARTIAL_COVERAGE',
    severity: 'warning',
    scope: 'application',
    countBy: 'application',
    label: 'Postulación con un solo evaluador',
    hint: 'Queda bajo el mínimo del concurso. Falta completar el segundo.',
  },
  OVER_QUOTA: {
    code: 'OVER_QUOTA',
    severity: 'warning',
    scope: 'evaluator',
    countBy: 'evaluator',
    label: 'Evaluador por sobre su cupo',
    hint: 'Se importa igual, pero quedará con más proyectos de los que admite el concurso.',
  },
  LOW_CONFIDENCE: {
    code: 'LOW_CONFIDENCE',
    severity: 'warning',
    scope: 'application',
    countBy: 'match',
    label: 'Match de confianza baja',
    hint: 'La afinidad entre el tema del proyecto y la expertiz declarada es débil.',
  },
  ALREADY_ASSIGNED: {
    code: 'ALREADY_ASSIGNED',
    severity: 'warning',
    scope: 'application',
    countBy: 'match',
    label: 'El evaluador ya estaba asignado',
    hint: 'No se duplica la asignación: el match se omite y se conserva la existente.',
  },

  // ── Informativos: no hay nada que corregir ────────────────────────────────
  EVALUATOR_UNMATCHED: {
    code: 'EVALUATOR_UNMATCHED',
    severity: 'info',
    scope: 'evaluator',
    countBy: 'evaluator',
    label: 'Evaluador sin ninguna postulación asignada',
    hint: 'Aparece en el archivo pero no recibió match. Queda disponible.',
  },
  APPLICATION_MISSING_FROM_FILE: {
    code: 'APPLICATION_MISSING_FROM_FILE',
    severity: 'info',
    scope: 'application',
    countBy: 'application',
    label: 'Postulación admisible ausente del archivo',
    hint: 'La empresa externa no la incluyó. Seguirá sin evaluadores.',
  },
};

/** Codes in the order the issues panel lists them. */
export const IMPORT_ISSUE_ORDER: ImportIssueCode[] = Object.keys(
  IMPORT_ISSUE_CATALOG
) as ImportIssueCode[];

export const isBlocking = (code: ImportIssueCode): boolean =>
  IMPORT_ISSUE_CATALOG[code].severity === 'error';

export const severityOf = (code: ImportIssueCode): ImportIssueSeverity =>
  IMPORT_ISSUE_CATALOG[code].severity;

/** Badge variant used for each severity, per the kit's colour semantics. */
export const SEVERITY_VARIANT: Record<
  ImportIssueSeverity,
  'danger' | 'warning' | 'info'
> = {
  error: 'danger',
  warning: 'warning',
  info: 'info',
};

export const SEVERITY_LABEL: Record<ImportIssueSeverity, string> = {
  error: 'No se importa',
  warning: 'Revisar',
  info: 'Informativo',
};

/** Singular / plural noun for each counting unit, used in the issues panel. */
export const UNIT_NOUN: Record<ImportIssueUnit, [string, string]> = {
  application: ['postulación', 'postulaciones'],
  match: ['match', 'matches'],
  evaluator: ['evaluador', 'evaluadores'],
};

export const countLabel = (unit: ImportIssueUnit, count: number): string => {
  const [one, many] = UNIT_NOUN[unit];
  return `${count} ${count === 1 ? one : many}`;
};
